/**
 * Comparison content loader.
 *
 * Mirrors `src/content/glossary/index.ts`: markdown files with YAML
 * frontmatter, eagerly globbed at build time, validated hard so a malformed
 * entry fails the build rather than shipping a broken page.
 *
 * Each entry is a single-vendor deep dive that answers "is there an
 * alternative to X" for one specific competitor. `/why-anter` is the hub
 * comparison page (multi-vendor table); these are the spokes — deeper on one
 * vendor, with their own FAQ, not a restatement of the hub's table rows. Every
 * comparative claim must be grounded in that vendor's own current
 * documentation (or a cited independent review), matching the "claim hygiene"
 * rule already enforced on `/why-anter`.
 */
import { parse as parseYaml } from "yaml";

type ComparisonFrontmatter = {
  term: string;
  slug: string;
  /** One or two sentences. Doubles as the meta description and the quotable direct-answer block. */
  shortDefinition: string;
  category: string;
  vendor: string;
  aliases?: string[];
  related?: string[];
  docsRefs?: { label: string; href: string }[];
  faq?: { question: string; answer: string }[];
  updatedAt: string;
  draft?: boolean;
};

export type ComparisonDocsRef = { label: string; href: string };
export type ComparisonFaqEntry = { question: string; answer: string };

export type Comparison = {
  term: string;
  slug: string;
  shortDefinition: string;
  category: string;
  vendor: string;
  aliases: string[];
  related: string[];
  docsRefs: ComparisonDocsRef[];
  faq: ComparisonFaqEntry[];
  updatedAt: string;
  draft: boolean;
  content: string;
};

const comparisonModules = import.meta.glob("./terms/*.md", {
  eager: true,
  import: "default",
  query: "?raw",
}) as Record<string, string>;

function splitFrontmatter(raw: string, filePath: string): { frontmatter: string; content: string } {
  const normalized = raw.replace(/\r\n/g, "\n");
  const match = normalized.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!match || !match[1]) {
    throw new Error(`Comparison "${filePath}" is missing a valid frontmatter block.`);
  }
  return { frontmatter: match[1], content: (match[2] ?? "").trim() };
}

function assertString(value: unknown, field: string, filePath: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`Comparison "${filePath}" has invalid "${field}" value.`);
  }
  return value.trim();
}

function assertDate(value: unknown, field: string, filePath: string): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split("T")[0] ?? value.toISOString();
  }
  const parsed = assertString(value, field, filePath);
  if (Number.isNaN(Date.parse(parsed))) {
    throw new Error(`Comparison "${filePath}" has invalid date in "${field}".`);
  }
  return parsed;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string" && v.trim().length > 0)
    : [];
}

function buildComparisons(): Comparison[] {
  const slugSet = new Set<string>();

  const comparisons = Object.entries(comparisonModules).map(([filePath, raw]) => {
    const { frontmatter, content } = splitFrontmatter(raw, filePath);
    const parsed = parseYaml(frontmatter) as ComparisonFrontmatter | null;
    if (!parsed || typeof parsed !== "object") {
      throw new Error(`Comparison "${filePath}" frontmatter could not be parsed.`);
    }

    const slug = assertString(parsed.slug, "slug", filePath);
    if (slugSet.has(slug)) {
      throw new Error(`Duplicate comparison slug "${slug}" found in "${filePath}".`);
    }
    slugSet.add(slug);

    // Same quality bar as the glossary, for the same reason: a thin comparison
    // page is a scaled-content risk that drags down every page on the site.
    if (content.length < 600) {
      throw new Error(
        `Comparison "${filePath}" is too thin (${content.length} chars). ` +
          `Write a real deep dive or delete it — stubs hurt the whole site.`
      );
    }

    const docsRefs = Array.isArray(parsed.docsRefs)
      ? parsed.docsRefs.filter(
          (ref): ref is ComparisonDocsRef =>
            !!ref && typeof ref.label === "string" && typeof ref.href === "string"
        )
      : [];
    if (docsRefs.length === 0) {
      throw new Error(`Comparison "${filePath}" must link at least one docs page.`);
    }

    const faq = Array.isArray(parsed.faq)
      ? parsed.faq.filter(
          (entry): entry is ComparisonFaqEntry =>
            !!entry && typeof entry.question === "string" && typeof entry.answer === "string"
        )
      : [];
    if (faq.length === 0) {
      throw new Error(
        `Comparison "${filePath}" must include at least one FAQ entry — that's the page's GEO surface.`
      );
    }

    return {
      term: assertString(parsed.term, "term", filePath),
      slug,
      shortDefinition: assertString(parsed.shortDefinition, "shortDefinition", filePath),
      category: assertString(parsed.category, "category", filePath),
      vendor: assertString(parsed.vendor, "vendor", filePath),
      aliases: stringArray(parsed.aliases),
      related: stringArray(parsed.related),
      docsRefs,
      faq,
      updatedAt: assertDate(parsed.updatedAt, "updatedAt", filePath),
      draft: parsed.draft === true,
      content,
    } satisfies Comparison;
  });

  return comparisons.sort((a, b) => a.term.localeCompare(b.term));
}

export const allComparisons = buildComparisons();
export const publishedComparisons = allComparisons.filter((c) => !c.draft);
export const visibleComparisons = import.meta.env.PROD ? publishedComparisons : allComparisons;

export function getComparisonBySlug(slug: string): Comparison | undefined {
  return visibleComparisons.find((c) => c.slug === slug);
}
